import { useEffect, useState } from 'react';
import { RouterProvider } from '@tanstack/react-router';
import { useLiveQuery } from 'dexie-react-hooks';
import { Toaster } from 'sonner';
import { db } from '@/db/dexie';
import { useSession } from '@/state/session';
import { bootstrap } from '@/auth/bootstrap';
import { NotAllowed, SignIn } from '@/auth/SignIn';
import { Onboarding } from '@/auth/Onboarding';
import { TooltipProvider } from '@/components/ui/overlay';
import { router } from '@/routes/router';

function Splash() {
  return (
    <div className="flex h-full items-center justify-center bg-surface-1" aria-busy="true">
      <img src="/icons/icon.svg" alt="Karya" width={56} height={56} className="animate-pulse" />
    </div>
  );
}

function Workspace() {
  const userId = useSession((s) => s.userId);
  const [skipped, setSkipped] = useState(false);
  const state = useLiveQuery(async () => {
    const [profile, spaces] = await Promise.all([
      db().profiles.get(userId ?? ''),
      db().spaces.count(),
    ]);
    return { onboarded: Boolean(profile?.onboarded_at) || spaces > 0 };
  }, [userId]);
  if (!state) return <Splash />;
  if (!state.onboarded && !skipped)
    return (
      <Onboarding
        onDone={(goImport) => {
          setSkipped(true);
          if (goImport)
            void router.navigate({ to: '/settings/$section', params: { section: 'import' } });
        }}
      />
    );
  return <RouterProvider router={router} />;
}

export function App() {
  const { mode, ready } = useSession();
  useEffect(() => {
    void bootstrap();
  }, []);
  let body;
  if (mode === 'loading' || !ready) body = <Splash />;
  else if (mode === 'signed-out') body = <SignIn />;
  else if (mode === 'not-allowed') body = <NotAllowed email={useSession.getState().email} />;
  else body = <Workspace />;
  return (
    <TooltipProvider>
      {body}
      <Toaster position="bottom-center" toastOptions={{ className: 'font-sans' }} />
    </TooltipProvider>
  );
}
